import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalText from "../common/LocalText";
import { getRecentActivity } from "../../api/activityApi";

const PAGE_SIZE = 10;

const actionMeta = {
  created: { Icon: CheckCircleOutlineIcon, color: "#4ade80", bg: "rgba(34,197,94,0.18)" },
  updated: { Icon: EditOutlinedIcon,       color: "#fb923c", bg: "rgba(251,146,60,0.18)" },
  deleted: { Icon: DeleteOutlineIcon,      color: "#f87171", bg: "rgba(248,113,113,0.18)" },
};

const getActionMeta = (action) => actionMeta[(action ?? "").toLowerCase()] ?? actionMeta.created;

const COLUMNS = ["", "Entity Type", "Entity Name", "Action", "Date & Time"];

const ActivityLogDialog = ({ open, onClose }) => {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [pageIndex, setPageIndex]     = useState(0);
  const [cursorHistory, setCursorHistory] = useState([0]);
  const [hasMore, setHasMore]         = useState(false);

  const loadPage = useCallback((index, history) => {
    const cursor = history[index] ?? 0;
    setLoading(true);
    setRows([]);
    getRecentActivity(PAGE_SIZE, cursor)
      .then(res => {
        const data = res.data;
        const items   = Array.isArray(data) ? data : (data?.items ?? []);
        const more    = Array.isArray(data) ? false : (data?.hasMore ?? false);
        const nextCur = Array.isArray(data) ? 0    : (data?.nextCursor ?? 0);
        setRows(items);
        setHasMore(more);
        if (more && history.length <= index + 1) {
          setCursorHistory(prev => {
            const next = [...prev];
            next[index + 1] = nextCur;
            return next;
          });
        }
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    const initial = [0];
    setPageIndex(0);
    setCursorHistory(initial);
    setHasMore(false);
    loadPage(0, initial);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    const next = pageIndex + 1;
    setPageIndex(next);
    loadPage(next, cursorHistory);
  };

  const handlePrev = () => {
    const prev = pageIndex - 1;
    setPageIndex(prev);
    loadPage(prev, cursorHistory);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", minHeight: { xs: 'auto', sm: 520 }, maxHeight: { xs: '92vh', sm: 'none' } } }}
    >
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span" sx={{ display: "block" }}>
          <LocalText>Activity Log</LocalText>
        </Typography>
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: "absolute", right: 10, top: 12, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell
                  key={col}
                  sx={{ fontWeight: 700, bgcolor: "#f5f7fa", color: "text.secondary", fontSize: 12 }}
                >
                  {col && <LocalText>{col}</LocalText>}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton variant="rounded" width={28} height={28} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  <TableCell><Skeleton variant="text" width={90} /></TableCell>
                </TableRow>
              ))
            ) : (Array.isArray(rows) ? rows : []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  <LocalText>No activity found.</LocalText>
                </TableCell>
              </TableRow>
            ) : (
              (Array.isArray(rows) ? rows : []).map((item, i) => {
                const { Icon: ActionIcon, color: iconColor, bg: iconBg } = getActionMeta(item.action);
                return (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Box sx={{ p: 0.6, borderRadius: 1.5, bgcolor: iconBg, display: "inline-flex" }}>
                        <ActionIcon sx={{ fontSize: 16, color: iconColor, display: "block" }} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {item.entityType ? <LocalText>{item.entityType}</LocalText> : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                      {item.entityName
                        ? <LocalText>{item.entityName}</LocalText>
                        : item.entityId ? `#${item.entityId}` : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, textTransform: "capitalize" }}>
                      {item.action ? <LocalText>{item.action}</LocalText> : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {item.performedAt ? new Date(item.performedAt).toLocaleString() : "—"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button
          variant="outlined"
          size="small"
          disabled={pageIndex === 0 || loading}
          onClick={handlePrev}
        >
          <LocalText>Previous</LocalText>
        </Button>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
          <LocalText>Page</LocalText> {pageIndex + 1}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={!hasMore || loading}
          onClick={handleNext}
        >
          <LocalText>Next</LocalText>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityLogDialog;
